import { auth } from "@/firebase";
import { defineStore } from "pinia";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  signOut,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import { useUserStore } from "./user";
import { createPrefixedLogger } from "@/utils/logger";

const logger = createPrefixedLogger("AUTH");

export const useAuthStore = defineStore("auth", {
  state: () => ({
    loggedIn: null,
    session: null,
  }),

  actions: {
    async registerWithEmail({ email, pwd }) {
      try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, pwd);
        this.session = userCredential.user;
        await sendEmailVerification(this.session);
        logger.info("Usuario registrado:", email);
        return userCredential.user.uid;
      } catch (error) {
        const errorMessages = {
          "auth/email-already-in-use": "El correo ya esta en uso",
          "auth/invalid-email": "Correo invalido",
          "auth/operation-not-allowed": "Operacion no permitida",
          "auth/weak-password": "Contrasena debil",
        };
        const message = errorMessages[error.code] || "Error al crear usuario";
        logger.error("Error en registro:", error.code);
        throw new Error(message);
      }
    },

    async logInUser({ email, pwd }) {
      try {
        const userStore = useUserStore();
        const userCredential = await signInWithEmailAndPassword(auth, email, pwd);

        this.session = userCredential.user;
        this.loggedIn = new Date().getTime();

        userStore.setUser(this.session);
        logger.info("Usuario autenticado:", email);

        return true;
      } catch (error) {
        const errorMessages = {
          "auth/invalid-credential": "Credenciales invalidas",
          "auth/user-disabled": "Usuario deshabilitado",
          "auth/user-not-found": "Usuario no encontrado",
          "auth/wrong-password": "Contrasena incorrecta",
        };
        const message = errorMessages[error.code] || "Error al iniciar sesion";
        logger.error("Error en login:", error.code);
        throw new Error(message);
      }
    },

    async signOutUser() {
      try {
        await signOut(auth);
        const userStore = useUserStore();
        userStore.clearUser();
        this.session = null;
        this.loggedIn = null;
        logger.info("Sesion cerrada");
        return true;
      } catch (error) {
        logger.error("Error al cerrar sesion:", error);
        throw new Error("Error al cerrar sesion");
      }
    },

    async setSessionPersistence(type = "SESSION") {
      try {
        const persistenceType =
          type === "LOCAL" ? browserLocalPersistence : browserSessionPersistence;
        await setPersistence(auth, persistenceType);
        return true;
      } catch (error) {
        logger.error("Error configurando persistencia:", error);
        throw error;
      }
    },

    setSession(user) {
      this.session = user;
      if (user) {
        this.loggedIn = new Date().getTime();
      }
    },
  },

  getters: {
    isAuthenticated: (state) => state.session !== null,
    currentUser: (state) => state.session,
    userEmail: (state) => state.session?.email || null,
  },

  persist: true,
});
